import React from 'react';
import './App.css';
import ProtectedLayout from './components/layout/ProtectedLayout';
import { ToastProvider, useToast } from './components/ui/Toaster';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { MessageBoxProvider } from './components/ui/MessageBox';
import { AuthProvider, useAuth } from './lib/auth';
import AuthLoginPage from './features/auth/login';

const AppContent: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const toast = useToast();

  // Keep URL in sync with auth state: unauthenticated -> /login, authenticated -> /dashboard (if on /login)
  React.useEffect(() => {
    const path = window.location.pathname;
    if (!isAuthenticated) {
      if (path !== '/login') {
        window.history.replaceState({}, '', '/login');
      }
    } else {
      if (path === '/login' || path === '/') {
        window.history.replaceState({}, '', '/dashboard');
      }
    }
  }, [isAuthenticated]);

  // Handle back/forward navigation across auth boundary
  React.useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname;
      if (!isAuthenticated && path !== '/login') {
        window.history.replaceState({}, '', '/login');
      } else if (isAuthenticated && path === '/login') {
        window.history.replaceState({}, '', '/dashboard');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isAuthenticated]);

  // Global admin notifications via SSE (transaction_created, etc.)
  React.useEffect(() => {
    if (!isAuthenticated) return;
    // Prefer explicit notification base, then API base, then default to backend behind /admin/api on same origin
    const notifBase = (
      process.env.REACT_APP_NOTIFICATIONS_BASE ||
      process.env.REACT_APP_API_BASE ||
      (window.location && window.location.origin ? window.location.origin + '/admin/api' : 'http://localhost:3002')
    ).toString();
    const url = notifBase.replace(/\/$/, '') + '/notifications/stream';
    const es = new EventSource(url);

    const onTransactionCreated = (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data || '{}');
        // Backend emits a Notification entity; detailed data (qty, unitPrice, etc.) is under payload.data
        const title = payload?.title || 'Transaction created';
        const message = payload?.message || '';
        if (message) {
          toast.success(`${title}: ${message}`);
          return;
        }
        const d = payload?.data || {};
        const qty = Number(d?.qty ?? 1);
        const currency = d?.currency || 'IDR';
        const unit = Number(d?.unitPrice ?? 0);
        const total = unit * qty;
        const product = d?.productName || d?.productId || 'Unknown product';
        const machine = d?.machineName || d?.machineId ? ` at ${d?.machineName || d?.machineId}` : '';
        toast.success(`New transaction${machine}: ${product} x${qty} — ${currency} ${total.toLocaleString()}`);
      } catch (_) {}
    };

    es.addEventListener('transaction_created', onTransactionCreated as any);

    return () => {
      es.removeEventListener('transaction_created', onTransactionCreated as any);
      es.close();
    };
  }, [isAuthenticated, toast]);

  if (!isAuthenticated) return <AuthLoginPage />;
  return <ProtectedLayout onLogout={logout} />;
};

function App() {
  return (
    <ToastProvider>
      <ConfirmProvider>
        <MessageBoxProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </MessageBoxProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
